import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { fetchViewMembers } from "../redux/slices/buyerNetworkSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Pagination,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Breadcrumbs,
  Link as MUILink,
  Divider,
} from "@mui/material";
import PageTitle from "../Components/PageTitle";

const BuyerViewMembers = () => {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const buyerCategoryId = state?.buyerCategoryId;
  const buyerCategoryName = state?.buyerCategoryName;
  const buyerCategoryColor = state?.buyerCategoryColor;
  const { buyers, membersCount, pagination, loading, error } = useSelector(
    (state) => state.buyerNetwork,
  );
  // Local state for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (user?._id && buyerCategoryId) {
      dispatch(
        fetchViewMembers({
          buyerCategory: buyerCategoryId,
          seller: user._id,
          page: currentPage,
          limit: itemsPerPage,
        }),
      );
    }
  }, [dispatch, user?._id, buyerCategoryId, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const memberCount = membersCount || 0;

  const connectionData = Array.isArray(buyers)
    ? buyers.flatMap((buyer) => buyer.connectionData || [])
    : buyers?.connectionData || [];

  //   const memberCount = membersCount || connectionData.length || 0;

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getTierStyles = (tier) => {
    const tierStyles = {
      "Gold Wholesaler": { color: "#ff9800", bgColor: "#fff3e0" },
      "Silver Partner": { color: "#2196f3", bgColor: "#e3f2fd" },
      "Platinum Distributor": { color: "#9c27b0", bgColor: "#f3e5f5" },
      "Standard Retailer": { color: "#666", bgColor: "#f5f5f5" },
      Premium: { color: "#4CAF50", bgColor: "#e8f5e9" },
      Basic: { color: "#607d8b", bgColor: "#eceff1" },
      "N/A": { color: "#9e9e9e", bgColor: "#f5f5f5" },
      default: { color: "#5b5fc7", bgColor: "#f0f0ff" },
    };

    return tierStyles[tier] || tierStyles["default"];
  };

  const formatStatus = (status) => {
    const statusStyles = {
      Accepted: { color: "#4CAF50", text: "Active" },
      Pending: { color: "#FF9800", text: "Pending" },
      Rejected: { color: "#F44336", text: "Rejected" },
      default: { color: "#9E9E9E", text: status || "Unknown" },
    };

    return statusStyles[status] || statusStyles["default"];
  };

  return (
    <>
      <PageTitle
        title="Buyer Category Members - BizBridge"
        description="View and manage buyers connected under a specific buyer category. Track member details, connection status and manage your buyer network efficiently."
        keywords="buyer category members, connected buyers, buyer network, seller dashboard, B2B buyers, buyer management, category wise buyers, BizBridge"
        url="/seller-dashboard/buyer-view-members"
      />

      <Box sx={{ py: { xs: 1, sm: 2 } }}>
        {/* Breadcrumb */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            width: "fit-content",
            backgroundColor: "#fff",
            // ml: { xs: 1, sm: 2.9 },
            padding: "13px",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <MUILink
            component={Link}
            to="/seller-dashboard/buyer-category"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Buyer Category
          </MUILink>
          <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
            View Members
          </Typography>
        </Breadcrumbs>

        <Divider sx={{ my: 2 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            // alignItems: "center",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 600, mb: 0.5 }}>
              Connected Buyers
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "start",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {memberCount} {memberCount === 1 ? "member" : "members"}
              </Typography>

              {buyerCategoryName && (
                <>
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      backgroundColor: "#ccc",
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: buyerCategoryColor,
                      fontWeight: 500,
                    }}
                  >
                    {buyerCategoryName}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography>Loading buyers...</Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="error">Error: {error}</Typography>
          </Box>
        )}

        {!loading && !error && (
          <Paper sx={{ overflow: "hidden" }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table
                sx={{
                  minWidth: 650,
                }}
              >
                <TableHead sx={{ backgroundColor: buyerCategoryColor }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#fff",
                        fontSize: 14,
                        py: 2,
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#fff",
                        fontSize: 14,
                        py: 2,
                      }}
                    >
                      BUYER NAME
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#fff",
                        fontSize: 14,
                        py: 2,
                      }}
                    >
                      EMAIL
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#fff",
                        fontSize: 14,
                        py: 2,
                      }}
                    >
                      PHONE
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#fff",
                        fontSize: 14,
                        py: 2,
                      }}
                    >
                      STATUS
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {connectionData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography color="text.secondary">
                          No buyers found in this category.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    connectionData.map((member, index) => {
                      const buyer = member.buyer || {};
                      const tierStyles = getTierStyles(
                        buyerCategoryName || "N/A",
                      );
                      const statusInfo = formatStatus(member.status);
                      const initials = getInitials(buyer.name);

                      return (
                        <TableRow
                          key={member._id}
                          sx={{
                            "&:hover": { backgroundColor: "#fafafa" },
                            borderBottom:
                              index < connectionData.length - 1
                                ? "1px solid #e0e0e0"
                                : "none",
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>{index + 1}</TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              {initials ? (
                                <Avatar
                                  alt={buyer?.name}
                                  src={buyer?.profileImage?.url}
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    backgroundColor: buyerCategoryColor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: 600,
                                    fontSize: 14,
                                  }}
                                >
                                  {initials}
                                </Avatar>
                              ) : null}
                              <Typography sx={{ fontWeight: 500 }}>
                                {buyer.name || "N/A"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography color="text.secondary">
                              {buyer.email || member.buyerEmail || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography>
                              {buyer.phone || member.buyerPhone || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: `${statusInfo.color}15`,
                                border: `1px solid ${statusInfo.color}30`,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: statusInfo.color,
                                  mr: 1,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: statusInfo.color,
                                }}
                              >
                                {statusInfo.text}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        )}

        {!loading && !error && pagination.total > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total} entries
              </Typography>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={itemsPerPage}
                  label="Items per page"
                  onChange={handleItemsPerPageChange}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack spacing={2}>
              <Pagination
                count={pagination.totalPages}
                page={currentPage}
                onChange={handlePageChange}
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: buyerCategoryColor || "#1976d2",
                  },
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: buyerCategoryColor || "#1976d2",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: buyerCategoryColor || "#1976d2",
                      opacity: 0.9,
                    },
                  },
                  "& .MuiPaginationItem-root:hover": {
                    backgroundColor:
                      `${buyerCategoryColor}20` || "rgba(25, 118, 210, 0.12)",
                  },
                }}
                showFirstButton
                showLastButton
              />
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
};

export default BuyerViewMembers;
